/**
 * Motores de observabilidad — API estable para FM, BH y platform-core.
 * La persistencia real la implementa cada app vía registerPlatformIngest().
 */
import {
  buildObservabilityEnvelope,
  resolveObservabilityDomain,
  shouldPersistDomain,
  shouldSampleTrace,
  type ObservabilityContext,
  type ObservabilityDomain,
  type StructuraApp,
} from "./observability";

export type PlatformObserveInput = {
  domain?: ObservabilityDomain;
  level: "info" | "warn" | "error";
  event: string;
  ctx?: Partial<ObservabilityContext>;
  meta?: Record<string, unknown>;
  entityType?: string;
  entityId?: string;
};

export type PlatformIngest = (input: PlatformObserveInput) => void;

let ingest: PlatformIngest | null = null;

export function registerPlatformIngest(handler: PlatformIngest): void {
  ingest = handler;
}

function dispatch(input: PlatformObserveInput): void {
  if (ingest) {
    ingest(input);
    return;
  }
  const ctx = input.ctx ?? {};
  const domain = input.domain ?? resolveObservabilityDomain(input.level, input.event);
  const envelope = buildObservabilityEnvelope({
    domain,
    level: input.level,
    event: input.event,
    ctx: {
      app: (ctx.app ?? "fm") as StructuraApp,
      environment: ctx.environment ?? "development",
      releaseVersion: ctx.releaseVersion ?? "unknown",
      releaseChannel: ctx.releaseChannel ?? "dev",
      releaseCommit: ctx.releaseCommit ?? null,
      tenantId: ctx.tenantId ?? null,
      workspaceId: ctx.workspaceId ?? ctx.tenantId ?? null,
      actorUserId: ctx.actorUserId ?? null,
      requestId: ctx.requestId,
      correlationId: ctx.correlationId,
    },
    meta: input.meta,
  });
  const line = JSON.stringify(envelope);
  if (input.level === "error") console.error(line);
  else if (input.level === "warn") console.warn(line);
  else console.info(line);
}

/** Auditoría crítica — siempre persistir en app ingest (dominio audit). */
export function auditAction(
  action: string,
  ctx: Partial<ObservabilityContext>,
  meta?: Record<string, unknown>,
): void {
  capturePlatformEvent({
    domain: "audit",
    level: "info",
    event: action.startsWith("audit.") ? action : `audit.${action}`,
    ctx,
    meta,
  });
}

/** Evento de plataforma (errores, warnings, business allowlisted). */
export function capturePlatformEvent(input: PlatformObserveInput): void {
  dispatch(input);
}

/** Métrica agregada — dominio metric (persistencia permanente sugerida). */
export function captureMetric(
  name: string,
  value: number,
  ctx: Partial<ObservabilityContext>,
  meta?: Record<string, unknown>,
): void {
  dispatch({
    domain: "metric",
    level: "info",
    event: name.startsWith("metric.") ? name : `metric.${name}`,
    ctx,
    meta: { ...meta, value },
  });
}

/** Trace muestreado — no persiste todos los requests. */
export function captureTrace(
  name: string,
  ctx: Partial<ObservabilityContext>,
  meta?: Record<string, unknown> & {
    durationMs?: number;
    failed?: boolean;
    path?: string;
    paymentRelated?: boolean;
  },
): void {
  const durationMs = meta?.durationMs;
  if (
    !shouldSampleTrace({
      failed: meta?.failed,
      slowMs: durationMs,
      paymentRelated: meta?.paymentRelated,
      path: meta?.path,
    })
  ) {
    return;
  }
  dispatch({
    domain: "trace",
    level: meta?.failed ? "error" : "info",
    event: name.startsWith("trace.") ? name : `trace.${name}`,
    ctx,
    meta,
  });
}

declare global {
  interface Window {
    __structuraTrack?: (event: string, meta?: Record<string, unknown>) => void;
  }
}

/** Tracking frontend — no-op en SSR; apps pueden registrar __structuraTrack. */
export function trackEvent(
  event: string,
  meta?: Record<string, unknown> & Partial<ObservabilityContext>,
): void {
  if (typeof window === "undefined") return;
  if (typeof window.__structuraTrack === "function") {
    window.__structuraTrack(event, meta);
    return;
  }
  if (import.meta.env?.DEV) {
    console.debug("[track]", event, meta);
  }
}

export { shouldPersistDomain, shouldSampleTrace };
